'use client';

import { useState } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import { RoomInfoPanel } from '@/components/public/room-info-panel';
import { ImageLightbox } from '@/components/public/image-lightbox';
import type { LightboxImage } from '@/components/public/image-lightbox';
import type { Room } from '@/types/database';

export default function Room38Content({ room }: { room: Room }) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const t = useTranslations('roomContent.room-38');

  const GALLERY_IMAGES: LightboxImage[] = [
    { src: '/design-images/uploads/1778233849006-38_ch__1.jpg', alt: t('alt.interior'), width: 1920, height: 1280 },
    { src: '/design-images/uploads/1778233477629-var_2468-butorfestes_vargyasi-sutoek_vargyasi.jpg', alt: t('alt.cabinet'), width: 1000, height: 1400 },
    { src: '/design-images/rooms/room-38/detail.jpg', alt: t('alt.detail'), width: 1920, height: 1283 },
    { src: '/design-images/uploads/1778234352172-38_ch__3.jpg', alt: t('alt.bathroom'), width: 1920, height: 1280 },
  ];

  return (
    <div id="room-content" className="w-full flex flex-col">
      {/* White spacer between hero and first section */}
      <div className="bg-white h-2 w-full" />

      {/* Section: Room Benefits */}
      <section className="bg-white w-full">
        <div className="max-w-[1500px] mx-auto w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div className="w-full flex items-center py-6 px-6 md:px-8 lg:px-12">
              <div className="text-black w-full">
                <div className="tiptap-content text-base md:text-lg leading-relaxed">
                    <h1 className="font-heading font-bold mb-4 text-4xl md:text-5xl">
                      <strong>{t('benefits.titleLine1')}</strong>
                      <br />
                      <strong>{t('benefits.titleLine2')}</strong>
                    </h1>
                    <p className="text-base md:text-lg leading-relaxed font-light text-gray-700 mb-6">{t('benefits.p1')}</p>
                </div>
                <RoomInfoPanel room={room} />
              </div>
            </div>
            <div
              className="w-full h-full min-h-[400px] md:min-h-[500px] relative cursor-pointer"
              onClick={() => setLightboxIndex(0)}
            >
              <Image
                src="/design-images/uploads/1778233849006-38_ch__1.jpg"
                alt={t('alt.interior')}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </div>
          </div>
        </div>
      </section>
      <div className="bg-white h-2 w-full" />

      {/* Section: Side-by-side */}
      <section className="bg-white w-full">

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div
              className="w-full h-full min-h-[400px] md:min-h-[500px] relative cursor-pointer"
              onClick={() => setLightboxIndex(1)}
            >
              <Image
                src="/design-images/uploads/1778233477629-var_2468-butorfestes_vargyasi-sutoek_vargyasi.jpg"
                alt={t('alt.cabinet')}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </div>
            <div className="w-full flex items-center py-8 md:py-12 px-6 md:px-8 lg:px-12">
              <div className="text-black w-full">
                <div className="tiptap-content text-base md:text-lg leading-relaxed">
                    <h1 className="font-heading font-bold mb-6 text-4xl md:text-5xl">{t('story.titleLine1')}<br /><strong>{t('story.titleLine2')}</strong></h1>
                    <div className="w-16 h-1 bg-[#801515] mb-8" />
                    <p className="text-lg md:text-xl leading-relaxed font-light italic text-gray-700 mb-4">{t('story.p1')}</p>
                    <p className="text-lg md:text-xl leading-relaxed font-light italic text-gray-700 mb-4">{t('story.p2')}</p>
                    <p className="text-lg md:text-xl leading-relaxed font-light text-gray-700">{t('story.p3')}</p>
                    <div className="mt-8">
                      <Link
                        href="/activities#furniture"
                        className="group inline-flex items-center gap-3 px-[26px] py-5 bg-[#4A6741] hover:bg-[#3a5534] text-white font-serif text-lg rounded-full transition-all duration-500 shadow-xl hover:shadow-2xl hover:shadow-[#4A6741]/40 transform hover:scale-105"
                      >{t('story.cta')}<ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                </div>
              </div>
            </div>
        </div>

      </section>
      <div className="bg-white h-2 w-full" />

      {/* Section: Bathroom image */}
      <section className="bg-white w-full">
        <div className="w-full h-full min-h-[300px] md:min-h-[500px] relative cursor-pointer" onClick={() => setLightboxIndex(3)}>
          <Image
            src="/design-images/uploads/1778234352172-38_ch__3.jpg"
            alt={t('alt.bathroom')}
            fill
            className="object-cover"
            sizes="100vw"
          />
        </div>
      </section>
      <div className="bg-white h-2 w-full" />

      {/* Fullscreen gallery lightbox */}
      {lightboxIndex !== null && (
        <ImageLightbox
          images={GALLERY_IMAGES}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}
